/**
 * Background sync module for PWA functionality.
 * Handles syncing queued actions and state reconciliation.
 */

import { logger } from "./logger";
import { offlineStorage, type QueuedGameAction } from "./offline";
import { wsRegistry } from "./wsRegistry";
import type { GameState, ClientActionEnum } from "../types/game";

const MAX_RETRY_COUNT = 3;
const SYNC_DEBOUNCE_MS = 1000;

type OnlineStatusCallback = (online: boolean) => void;
type SyncCallback = (
  synced: boolean,
  failedActions: QueuedGameAction[],
) => void;

class BackgroundSync {
  private onlineCallbacks: OnlineStatusCallback[] = [];
  private syncCallbacks: SyncCallback[] = [];
  private isSyncing = false;
  private syncTimer: number | null = null;

  constructor() {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for online/offline events
    window.addEventListener("online", () => {
      logger.info("Network status changed to online");
      this.notifyOnlineStatus(true);
      this.scheduleSyncDebounced();
    });

    window.addEventListener("offline", () => {
      logger.info("Network status changed to offline");
      this.notifyOnlineStatus(false);
    });

    // Listen for service worker sync messages
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "SYNC_TRIGGERED") {
          logger.info("Background sync triggered by service worker");
          this.syncQueuedActions();
        }
      });
    }
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Register callback for online status changes
   */
  onOnlineStatusChange(callback: OnlineStatusCallback): () => void {
    this.onlineCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.onlineCallbacks.indexOf(callback);
      if (index > -1) {
        this.onlineCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Register callback for sync completion
   */
  onSyncComplete(callback: SyncCallback): () => void {
    this.syncCallbacks.push(callback);
    return () => {
      const index = this.syncCallbacks.indexOf(callback);
      if (index > -1) {
        this.syncCallbacks.splice(index, 1);
      }
    };
  }

  private notifyOnlineStatus(online: boolean): void {
    this.onlineCallbacks.forEach((callback) => {
      try {
        callback(online);
      } catch (error) {
        logger.error("Error in online status callback", { error });
      }
    });
  }

  private notifySyncComplete(
    synced: boolean,
    failedActions: QueuedGameAction[],
  ): void {
    this.syncCallbacks.forEach((callback) => {
      try {
        callback(synced, failedActions);
      } catch (error) {
        logger.error("Error in sync callback", { error });
      }
    });
  }

  private scheduleSyncDebounced(): void {
    if (this.syncTimer) {
      window.clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      this.syncQueuedActions();
    }, SYNC_DEBOUNCE_MS);
  }

  /**
   * Sync all queued actions to the server
   */
  async syncQueuedActions(): Promise<void> {
    if (!this.isOnline()) {
      logger.debug("Cannot sync: offline");
      return;
    }

    if (this.isSyncing) {
      logger.debug("Sync already in progress");
      return;
    }

    this.isSyncing = true;
    const failedActions: QueuedGameAction[] = [];

    try {
      const actions = await offlineStorage.getQueuedActions();

      if (actions.length === 0) {
        logger.debug("No actions to sync");
        this.notifySyncComplete(true, []);
        return;
      }

      logger.info("Starting sync of queued actions", { count: actions.length });

      for (const action of actions) {
        if (action.retryCount >= MAX_RETRY_COUNT) {
          logger.warn("Action exceeded max retries, removing", {
            id: action.id,
            action: action.action,
          });
          await offlineStorage.removeQueuedAction(action.id);
          failedActions.push(action);
          continue;
        }

        try {
          const success = await this.sendAction(action);

          if (success) {
            await offlineStorage.removeQueuedAction(action.id);
            logger.debug("Action synced successfully", {
              id: action.id,
              action: action.action,
            });
          } else {
            await offlineStorage.updateActionRetryCount(action.id);
            failedActions.push(action);
          }
        } catch (error) {
          logger.error("Error syncing action", {
            id: action.id,
            error,
          });
          await offlineStorage.updateActionRetryCount(action.id);
          failedActions.push(action);
        }
      }

      const allSynced = failedActions.length === 0;
      logger.info("Sync completed", {
        total: actions.length,
        synced: actions.length - failedActions.length,
        failed: failedActions.length,
      });

      this.notifySyncComplete(allSynced, failedActions);
    } catch (error) {
      logger.error("Error during sync process", { error });
      this.notifySyncComplete(false, failedActions);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Send a single action to the server
   */
  private async sendAction(action: QueuedGameAction): Promise<boolean> {
    logger.debug("Sending action to server", {
      id: action.id,
      action: action.action,
      sessionId: action.sessionId,
    });

    // Check if we have an active WebSocket connection for this session
    if (!wsRegistry.hasConnection(action.sessionId)) {
      logger.warn("No active WebSocket connection for session", {
        sessionId: action.sessionId,
      });
      return false;
    }

    // Send via WebSocket registry
    const success = wsRegistry.sendMessage(
      action.sessionId,
      action.action as ClientActionEnum,
      action.payload,
    );

    if (success) {
      logger.info("Action sent via WebSocket", {
        id: action.id,
        action: action.action,
        sessionId: action.sessionId,
      });
    }

    return success;
  }

  /**
   * Reconcile local state with server state
   * Returns the most up-to-date state
   */
  async reconcileState(
    local: GameState,
    server: GameState,
  ): Promise<GameState> {
    logger.info("Reconciling local and server states", {
      localRound: local.current_round,
      serverRound: server.current_round,
    });

    // Server state is always authoritative
    // However, we need to merge any local preferences or UI state

    // Strategy: Server wins for game data, local preferences are preserved
    const reconciled: GameState = {
      ...server,
      // Preserve any local UI state that doesn't conflict
      // (In this case, server state is fully authoritative)
    };

    logger.debug("State reconciled", {
      finalRound: reconciled.current_round,
    });

    return reconciled;
  }

  /**
   * Request background sync registration
   */
  async registerBackgroundSync(): Promise<boolean> {
    if (!("serviceWorker" in navigator)) {
      logger.warn("Service workers not supported");
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      if ("sync" in registration) {
        // TypeScript doesn't have types for Background Sync API yet
        const syncManager = (registration as unknown as { sync: SyncManager })
          .sync;
        await syncManager.register("sync-game-actions");
        logger.info("Background sync registered");
        return true;
      } else {
        logger.warn("Background sync not supported");
        return false;
      }
    } catch (error) {
      logger.error("Failed to register background sync", { error });
      return false;
    }
  }

  /**
   * Check if there are pending actions to sync
   */
  async hasPendingActions(): Promise<boolean> {
    const actions = await offlineStorage.getQueuedActions();
    return actions.length > 0;
  }

  /**
   * Get count of pending actions
   */
  async getPendingActionCount(): Promise<number> {
    const actions = await offlineStorage.getQueuedActions();
    return actions.length;
  }
}

// SyncManager interface for Background Sync API
interface SyncManager {
  register(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

// Export singleton instance
export const backgroundSync = new BackgroundSync();

// Export class for testing
export { BackgroundSync };
